import React from "react";
import { Dispatch, SetStateAction } from "react";
import { Button, Typography, Dialog }  from "@material-tailwind/react";
import { FormProvider, useForm } from 'react-hook-form'

import FormInput from "../../../../components/FormInput";
import { name_validation, year_validation } from "../../../../utils/inputValidations";
import { Vehicle } from "../../../../redux/vehicles/actions";
import { FormDatepickerInput } from "../../../../components/FormDatepickerInput";
import { FormTagInput } from "../../../../components/FormTagInput";

const DETALIS_FIELDS = [
    { n: "VIN", f: "vin", type: "text", placeholder: "Enter VIN" },
    { n: "Make", f: "make", type: "text", placeholder: "Enter Make" },
    { n: "Model", f: "model", type: "text", placeholder: "Enter Model" },
    { n: "Year", f: "year", type: "year", placeholder: "YYYY" },
    { n: "Trim", f: "trim", type: "text", placeholder: "Enter Trim", optional: true },
    { n: "Body Style", f: "body_style", type: "text", placeholder: "Enter Body Style", optional: true },
    { n: "Engine", f: "engine_type", type: "text", placeholder: "Enter Engine", optional: true },
    { n: "Miles", f: "miles", type: "number", placeholder: "Enter Miles" },
    { n: "Estimated Sale Price", f: "estimated_sale_price", type: "money", placeholder: "$0.00" },
    { n: "Date of Purchase", f: "purchase_date", type: "date", placeholder: "YYYY-MM-DD" },
    { n: "Purchase Price", f: "purchase_price", type: "money", placeholder: "$0.00" },
    { n: "Payment Method", f: "payment_method", type: "tags", options: ["cash", "floorplan", "other"], placeholder: "Cash, Floorplan, etc." },
    { n: "Purchase Location", f: "market", type: "text", placeholder: "Enter Purchase Location" },
    { n: "Parts Budget", f: "parts_budget", type: "money", placeholder: "$0.00" },
    { n: "Labor Budget", f: "labor_budget", type: "money", placeholder: "$0.00" },
];

interface DialogAddVehicleProps {
    isOpen: boolean;
    setIsOpen: Dispatch<SetStateAction<boolean>>;
    onSubmit: (vehicle: Vehicle) => void;
}

export const DialogAddVehicle: React.FC<DialogAddVehicleProps> = ({
    isOpen,
    setIsOpen,
    onSubmit,
}) => {
    const methods = useForm();

    const onFormSubmit = methods.handleSubmit(data => {
        onSubmit(data as Vehicle);
    });

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen} size="lg">
            <Dialog.Overlay>
                <Dialog.Content className="p-2 md:p-8">
                    <div className="flex flex-col gap-1 p-2 md:p-6 pb-4 border-b border-surface">
                        <Typography type="h5" color="default">
                            New Vehicle
                        </Typography>
                    </div>
                    <FormProvider {...methods}>
                        <form
                            className="flex flex-col p-2 md:p-6"
                            onSubmit={(e) => e.preventDefault()}
                            noValidate
                            autoComplete="off"
                        >
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-y-5 justify-stretch">
                                {DETALIS_FIELDS.map((field, idx) => (
                                    <div className="" key={idx}>
                                        {field.type === "date" ? (
                                            <FormDatepickerInput
                                                name={field.f}
                                                label={field.n}
                                                wrapperClass="mt-1 w-[95%]"
                                                placeholder={field.placeholder}
                                            />
                                        ) : field.type === "tags" ? (
                                            <FormTagInput
                                                id={field.f}
                                                name={field.f}
                                                label={field.n}
                                                wrapperClass="mt-1 w-[95%]"
                                                labelClass="mb-2 font-medium text-sm text-foreground"
                                                placeholder={field.placeholder}
                                                options={field.options?.map(option => ({
                                                    value: option,
                                                    label: option
                                                })) || []}
                                            />
                                        ) : field.type === "year" ? (
                                            <FormInput
                                                {...year_validation}
                                                validation={
                                                    field?.optional
                                                        ? { required: false }
                                                        : year_validation.validation
                                                }
                                                label={field.n}
                                                labelClass="mb-2 font-medium text-sm text-foreground"
                                                wrapperClass="mt-1 w-[95%]"
                                                name={field.f}
                                                type="number"
                                                placeholder={field.placeholder}
                                            />
                                        ) : (
                                            <FormInput
                                                {...name_validation}
                                                validation={
                                                    field?.optional
                                                        ? { required: false }
                                                        : name_validation.validation
                                                }
                                                label={field.n}
                                                labelClass="mb-2 font-medium text-sm text-foreground"
                                                wrapperClass="mt-1 w-[95%]"
                                                name={field.f}
                                                type={
                                                    field.type === "money" ? "number" : field.type
                                                }
                                                placeholder={field.placeholder}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </form>
                    </FormProvider>
                    <div className="flex gap-3 justify-end p-6 border-t border-surface">
                        <Button
                            variant="outline"
                            color="secondary"
                            onClick={() => setIsOpen(false)}
                            className="mr-1"
                        >
                            Cancel
                        </Button>
                        <Button variant="solid" color="primary" onClick={onFormSubmit}>
                            Confirm
                        </Button>
                    </div>
                </Dialog.Content>
            </Dialog.Overlay>
        </Dialog>
    );
};